import { getEnv } from '@/env'
import type { SectionKey } from '@/store/appStore'

export const APP_NAME        = getEnv('IT_MFE_APP_NAME')        || 'Portal Empresas'
export const APP_VERSION     = getEnv('IT_MFE_APP_VERSION')     || 'v1.0.0'
export const APP_ENVIRONMENT = getEnv('IT_MFE_APP_ENVIRONMENT') || 'DEV'

// ─── Routes ───────────────────────────────────────────────────────────────────
// Only real URL paths are kept here. Dashboard sections are identified by
// SectionKey (store/appStore.ts) and never appear in the browser URL.

export const ROUTES = {
  HOME:      '/',
  LOGIN:     '/login',
  NOT_FOUND: '*',
} as const

// ─── Menu structure ───────────────────────────────────────────────────────────
// Rules:
//   - type PAGE → appears in sidebar (if access.map[tag] set OR alwaysVisible)
//   - type OPTION → never in sidebar; used only by useAccess() canSee/canWrite
//   - superOnly → rendered only when identity.isKashioSuper = true
//   - alwaysVisible → shown to every authenticated user (no KSEC gate)
//
// Tags must match exactly the DigitalAssetItem.tag_code values in KSEC
// (seeded by seed-portal-empresa.ts).

export interface MenuSection {
  section: string
  items: MenuItem[]
  superOnly?: boolean
}

export interface MenuItem {
  tag: string
  name: string
  icon: string                 // lucide icon name
  type: 'PAGE' | 'OPTION'
  sectionKey?: SectionKey      // identifies which section to activate on click
  superOnly?: boolean          // visible only when isKashioSuper = true
  alwaysVisible?: boolean      // visible regardless of access map
  disabled?: boolean           // temporarily hidden from sidebar
}

export const MENU_STRUCTURE: MenuSection[] = [
  // ── General (always visible to authenticated users) ────────────────────────
  {
    section: 'General',
    items: [
      {
        tag:           'pe.dashboard',
        name:          'Dashboard',
        icon:          'BarChart2',
        type:          'PAGE',
        sectionKey:    'productos',
        alwaysVisible: true,
      },
    ],
  },

  // ── Mi Cuenta ─────────────────────────────────────────────────────────────
  {
    section: 'Mi Cuenta',
    items: [
      {
        tag:           'pe.perfil',
        name:          'Mi Perfil',
        icon:          'UserCircle',
        type:          'PAGE',
        alwaysVisible: true,
        disabled:      true, // TODO: reactivar cuando esté lista la sección
      },
    ],
  },

  // ── Organización ──────────────────────────────────────────────────────────
  {
    section: 'Organización',
    items: [
      {
        tag:           'pe.usuarios',
        name:          'Usuarios',
        icon:          'Users',
        type:          'PAGE',
        sectionKey:    'usuarios',
        alwaysVisible: true,
      },
      {
        tag:           'pe.roles',
        name:          'Roles',
        icon:          'ShieldCheck',
        type:          'PAGE',
        sectionKey:    'roles',
        alwaysVisible: true,
      },
    ],
  },

  // ── Plataforma (KashioSuper only) ─────────────────────────────────────────
  {
    section: 'Plataforma',
    superOnly: true,
    items: [
      {
        tag:        'PE.PLATFORM.ONBOARDING',
        name:       'Onboarding',
        icon:       'Rocket',
        type:       'PAGE',
        sectionKey: 'plataforma_onboarding',
        superOnly:  true,
      },
      {
        tag:        'PE.PLATFORM.ORGS',
        name:       'Organizaciones',
        icon:       'Globe',
        type:       'PAGE',
        sectionKey: 'plataforma_orgs',
        superOnly:  true,
      },
    ],
  },

  // ── Catálogo (KashioSuper only) ───────────────────────────────────────────
  {
    section: 'Catálogo',
    superOnly: true,
    items: [
      {
        tag:        'PE.CATALOG.ROLES',
        name:       'Roles',
        icon:       'Drama',
        type:       'PAGE',
        sectionKey: 'catalogo_roles',
        superOnly:  true,
      },
      {
        tag:        'PE.CATALOG.ASSETS',
        name:       'Assets',
        icon:       'Layers',
        type:       'PAGE',
        sectionKey: 'catalogo_assets',
        superOnly:  true,
      },
    ],
  },

  // ── Auditoría (KashioSuper only) ──────────────────────────────────────────
  {
    section: 'Auditoría',
    superOnly: true,
    items: [
      {
        tag:        'PE.AUDIT.PERMISSIONS',
        name:       'Auditoría',
        icon:       'Search',
        type:       'PAGE',
        sectionKey: 'auditoria',
        superOnly:  true,
      },
    ],
  },
]

export const PAGINATION_DEFAULTS = {
  PAGE: 1,
  LIMIT: 20,
} as const
